(() => {
  'use strict';
  if (customElements.get('simp-cart-content')) return;

  class SimpCartContent extends HTMLElement {
    connectedCallback() {
      this.lifecycle?.abort();
      this.lifecycle = new AbortController();
      this.readSequence = 0;
      this.disabledStates = new WeakMap();
      const options = { signal: this.lifecycle.signal };
      this.addEventListener('click', (event) => this.onClick(event), options);
      this.addEventListener('change', (event) => this.onChange(event), options);
      this.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' && event.target.matches('[data-cart-quantity]')) {
          event.preventDefault();
          this.onChange(event);
        }
      }, options);
      document.addEventListener('cart:update', (event) => {
        if (event.detail?.data?.source === 'simp-header-cart') return;
        const count = event.detail?.resource?.item_count ?? event.detail?.data?.itemCount;
        if (Number.isInteger(count)) this.syncCount(count);
        if (this.closest('.simp-header__cart-drawer')?.getAttribute('aria-hidden') === 'false') {
          if (this.updating) this.refreshAfterUpdate = true;
          else void this.refresh();
        }
      }, options);
      this.closest('[role="dialog"]')?.addEventListener('keydown', (event) => this.trapFocus(event), options);
      this.syncCount(Number(this.dataset.cartCount));
    }

    disconnectedCallback() {
      this.lifecycle?.abort();
      this.readController?.abort();
      this.readSequence++;
    }

    setBusy(busy) {
      this.busy = busy;
      this.toggleAttribute('aria-busy', busy);
      if (busy) this.setAttribute('aria-busy', 'true');
      this.querySelectorAll('button, input').forEach((control) => {
        if (busy) {
          if (!this.disabledStates.has(control)) this.disabledStates.set(control, control.disabled);
          control.disabled = true;
        } else if (this.disabledStates.has(control)) {
          control.disabled = this.disabledStates.get(control);
          this.disabledStates.delete(control);
        }
      });
    }

    syncCount(count) {
      if (!Number.isInteger(count) || count < 0) return;
      this.dataset.cartCount = String(count);
      this.closest('.simp-header')?.querySelectorAll('.simp-header__cart-toggle').forEach((button) => {
        if (button.classList.contains('simp-header__drawer-cart')) {
          const label = button.querySelector('span:not(.simp-header__icon):not(.simp-header__cart-count)');
          if (label) label.textContent = button.getAttribute('aria-label');
        }
        let badge = button.querySelector('.simp-header__cart-count');
        if (!badge) {
          badge = document.createElement('span');
          badge.className = 'simp-header__cart-count';
          button.appendChild(badge);
        }
        badge.textContent = String(count);
        badge.hidden = count === 0;
      });
    }

    showError(message) {
      const error = this.querySelector('[data-cart-error]');
      if (error) { error.textContent = message; error.hidden = !message; }
    }

    applyHTML(html) {
      if (!html || !this.isConnected) return false;
      const next = new DOMParser().parseFromString(html, 'text/html').getElementById(this.id);
      if (!next) return false;
      const scroll = this.querySelector('.simp-header__cart-lines')?.scrollTop || 0;
      this.replaceChildren(...next.childNodes);
      const list = this.querySelector('.simp-header__cart-lines');
      if (list) list.scrollTop = scroll;
      this.syncCount(Number(next.dataset.cartCount));
      return true;
    }

    async refresh() {
      if (this.updating) { this.refreshAfterUpdate = true; return; }
      this.readController?.abort();
      this.readController = new AbortController();
      const sequence = ++this.readSequence;
      this.setBusy(true);
      try {
        const url = new URL(window.location.href);
        url.hash = '';
        url.searchParams.set('section_id', this.dataset.sectionId);
        const response = await fetch(url, { signal: this.readController.signal, cache: 'no-store', headers: { Accept: 'text/html' } });
        if (!response.ok) throw new Error('cart-read-failed');
        const html = await response.text();
        if (sequence !== this.readSequence || !this.isConnected) return;
        if (!this.applyHTML(html)) throw new Error('cart-section-missing');
      } catch (error) {
        if (error.name !== 'AbortError' && sequence === this.readSequence && this.isConnected) this.showError(this.dataset.loadError);
      } finally {
        if (sequence === this.readSequence && this.isConnected) this.setBusy(false);
      }
    }

    onClick(event) {
      const button = event.target.closest('button[data-cart-adjust], button[data-cart-remove]');
      if (!button || button.disabled || this.busy) return;
      const row = button.closest('[data-cart-key]');
      const input = row?.querySelector('[data-cart-quantity]');
      if (!input) return;
      if (button.hasAttribute('data-cart-remove')) {
        void this.changeQuantity(row, 0, 'remove');
      } else {
        const quantity = Number(input.value) + Number(button.dataset.cartAdjust) * Number(input.step || 1);
        input.value = String(quantity);
        if (input.reportValidity()) void this.changeQuantity(row, quantity, button.dataset.cartAdjust);
        else input.value = input.defaultValue;
      }
    }

    onChange(event) {
      const input = event.target;
      if (!input.matches('[data-cart-quantity]') || input.disabled || this.busy) return;
      if (!input.reportValidity() || !Number.isSafeInteger(Number(input.value))) return;
      if (input.value === input.defaultValue) return;
      void this.changeQuantity(input.closest('[data-cart-key]'), Number(input.value), 'input');
    }

    async changeQuantity(row, quantity, focusKind) {
      if (this.busy || !row?.dataset.cartKey || !Number.isSafeInteger(quantity) || quantity < 0) return;
      const key = row.dataset.cartKey;
      this.readController?.abort();
      this.readSequence++;
      this.updating = true;
      this.showError('');
      this.setBusy(true);
      try {
        const sectionIds = [this.dataset.sectionId];
        document.querySelectorAll('cart-items-component[data-section-id]').forEach((node) => {
          if (!sectionIds.includes(node.dataset.sectionId) && sectionIds.length < 5) sectionIds.push(node.dataset.sectionId);
        });
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: key, quantity, sections: sectionIds, sections_url: window.location.pathname }),
        });
        const cart = await response.json();
        if (!response.ok || cart.errors) {
          const message = typeof cart.description === 'string' ? cart.description : typeof cart.errors === 'string' ? cart.errors : this.dataset.updateError;
          const error = new Error(message);
          error.userMessage = message;
          throw error;
        }
        if (!this.isConnected) return;
        this.syncCount(cart.item_count);
        const rendered = this.applyHTML(cart.sections?.[this.dataset.sectionId]);
        this.dispatchEvent(new CustomEvent('cart:update', { bubbles: true, detail: {
          resource: cart, sourceId: this.id,
          data: { source: 'simp-header-cart', itemCount: cart.item_count, sections: cart.sections || {} },
        } }));
        if (!rendered) this.refreshAfterUpdate = true;
        const live = this.querySelector('[data-cart-announcement]');
        if (live) live.textContent = this.dataset.updatedLabel;
        this.restoreFocus(key, focusKind);
      } catch (error) {
        if (this.isConnected) {
          row.querySelectorAll('[data-cart-quantity]').forEach((input) => { input.value = input.defaultValue; });
          this.showError(error.userMessage || this.dataset.updateError);
        }
      } finally {
        this.updating = false;
        if (this.isConnected) {
          this.setBusy(false);
          this.restoreFocus(key, focusKind);
          if (this.refreshAfterUpdate) { this.refreshAfterUpdate = false; void this.refresh(); }
        }
      }
    }

    restoreFocus(key, kind) {
      if (!this.contains(document.activeElement) && document.activeElement !== document.body) return;
      const row = Array.from(this.querySelectorAll('[data-cart-key]')).find((node) => node.dataset.cartKey === key);
      const selector = kind === 'input' ? '[data-cart-quantity]' : kind === 'remove' ? '[data-cart-remove]' : `[data-cart-adjust="${kind}"]`;
      (row?.querySelector(`${selector}:not(:disabled)`) || this.querySelector('[data-cart-quantity]:not(:disabled), .simp-header__cart-view'))?.focus();
    }

    trapFocus(event) {
      if (event.key !== 'Tab') return;
      const dialog = this.closest('[role="dialog"]');
      const controls = Array.from(dialog.querySelectorAll('a[href], button:not(:disabled), input:not(:disabled)')).filter((node) => node.getClientRects().length);
      const first = controls[0];
      const last = controls[controls.length - 1];
      if (event.shiftKey && event.target === first) { event.preventDefault(); last?.focus(); }
      else if (!event.shiftKey && event.target === last) { event.preventDefault(); first?.focus(); }
    }
  }

  customElements.define('simp-cart-content', SimpCartContent);
})();
